import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import payrollService from '../services/payrollService';
import employeesService from '../services/employeesService';
import { useNotification } from '../context/NotificationContext';
import EmployeeAutocomplete from './EmployeeAutocomplete';
import { FREQUENCIES, FREQUENCY_DAYS, buildPeriodOptions } from '../constants/payrollPeriods';
import { normalizeNumericInput, toNumber } from '../utils/numericInput';
import '../styles/general.css';

// Statuses whose payrolls are frozen. The backend rejects the write either
// way; disabling the inputs is the convenience half of the rule.
const LOCKED_STATUSES = ['pagado', 'cancelado'];

const AMOUNT_FIELDS = [
  'base_salary', 'extra_pay', 'bonus', 'bonus2',
  'social_security', 'savings', 'deduction',
];

const emptyForm = () => ({
  employee: '',
  period_code: '',
  period_start: '',
  period_end: '',
  payment_date: '',
  frequency: '',
  base_salary: '0.00',
  extra_pay: '0',
  bonus_description: '',
  bonus: '0',
  bonus2_description: '',
  bonus2: '0',
  social_security: '0',
  savings: '0',
  deduction_description: '',
  deduction: '0',
  bank_name: '',
  bank_account_number: '',
  payment_reference: '',
});

const PayrollForm = ({ initialData = null, onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const { notify } = useNotification();

  const [employees, setEmployees] = useState([]);
  // The full employee record, not the roster row. The list endpoint uses
  // EmployeeListSerializer, which omits daily_salary and the bank fields, so
  // the roster alone cannot drive the salary calculation or the bank snapshot.
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [formData, setFormData] = useState(() => ({
    ...emptyForm(),
    ...(initialData || {}),
  }));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const locked = LOCKED_STATUSES.includes(initialData?.status);

  useEffect(() => {
    const loadEmployees = async () => {
      try {
        const res = await employeesService.listEmployees({ showInactive: false });
        setEmployees(res.data.results || []);
      } catch (err) {
        console.error('Error loading employees:', err);
        notify.error(t('common.failedToLoad'));
      }
    };
    loadEmployees();
    // notify and t are stable enough for a mount-only load; re-running this on
    // every render would refetch the roster continuously.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const periodOptions = useMemo(
    () => buildPeriodOptions(formData.frequency),
    [formData.frequency]
  );

  /**
   * Base salary from the employee's daily rate and the frequency's day count.
   * Frequencies without a day count - proyecto, extemporal - produce zero,
   * because their amount is captured by hand.
   */
  const computeBaseSalary = useCallback((employee, frequency) => {
    const days = FREQUENCY_DAYS[frequency];
    if (!employee || !days) return '0.00';
    return (parseFloat(employee.daily_salary || 0) * days).toFixed(2);
  }, []);

  /**
   * Load the full employee record and apply everything derived from it.
   *
   * The roster row cannot be used directly: the list endpoint serialises
   * employees without daily_salary, bank_name or bank_account_number, so
   * computing from it silently produced a zero salary and empty bank fields.
   */
  const loadEmployeeDetail = useCallback(async (employeeId) => {
    try {
      const res = await employeesService.getEmployeeById(employeeId);
      setSelectedEmployee(res.data);
      return res.data;
    } catch (err) {
      console.error('Error loading employee detail:', err);
      notify.error(t('common.failedToLoad'));
      return null;
    }
  }, [notify, t]);

  const handleEmployeeSelect = async (employee) => {
    // Show the chosen name immediately; the derived values follow the fetch.
    setSelectedEmployee(employee);

    const detail = await loadEmployeeDetail(employee.id);
    if (!detail) return;

    setFormData((current) => ({
      ...current,
      employee: detail.id,
      // Snapshotted onto the payroll: the employee's own fields can change
      // later, and this records where this payment went.
      bank_name: detail.bank_name || '',
      bank_account_number: detail.bank_account_number || '',
      base_salary: computeBaseSalary(detail, current.frequency),
    }));
  };

  // On edit the payroll carries only the employee's id, so the detail is
  // fetched for the autocomplete's label and for the daily rate a later
  // frequency change needs. It deliberately does NOT touch formData: the
  // payroll's own stored salary and bank snapshot are what was agreed for
  // this payment and must not be silently replaced by today's employee record.
  useEffect(() => {
    if (initialData?.employee) {
      loadEmployeeDetail(initialData.employee);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData?.employee]);

  const handleFrequencyChange = (frequency) => {
    setFormData((current) => ({
      ...current,
      frequency,
      // Deliberately overwrites a manually edited salary. The field stays
      // editable so the value can be corrected afterwards.
      base_salary: computeBaseSalary(selectedEmployee, frequency),
      // The old code belongs to the old frequency's scheme.
      period_code: '',
    }));
  };

  const handleChange = (field) => (value) => {
    setFormData((current) => ({ ...current, [field]: value }));
  };

  const handleAmountChange = (field) => (value) => {
    setFormData((current) => ({
      ...current,
      [field]: normalizeNumericInput(value),
    }));
  };

  // Mirrors the backend formulas, which stay authoritative: the server
  // recomputes on save and ignores anything the client claims.
  const totals = useMemo(() => {
    const earnings =
      toNumber(formData.base_salary) + toNumber(formData.extra_pay) +
      toNumber(formData.bonus) + toNumber(formData.bonus2);
    const deductions =
      toNumber(formData.savings) + toNumber(formData.deduction) +
      toNumber(formData.social_security);
    return {
      earnings,
      deductions,
      // Capped at zero: a payroll never pays a negative amount.
      payable: Math.max(0, earnings - deductions),
    };
  }, [formData]);

  const validate = () => {
    const found = {};

    if (formData.period_start && formData.period_end
        && formData.period_start >= formData.period_end) {
      found.period_end = t('payroll.periodOrderError');
    }

    setErrors(found);
    return Object.keys(found).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (locked || !validate()) return;

    setSaving(true);
    try {
      // The three totals are never sent: they are read-only on the API and
      // recomputed server-side from these components.
      const payload = {
        employee: formData.employee,
        period_code: formData.period_code,
        period_start: formData.period_start,
        period_end: formData.period_end,
        payment_date: formData.payment_date,
        frequency: formData.frequency,
        bonus_description: formData.bonus_description,
        bonus2_description: formData.bonus2_description,
        deduction_description: formData.deduction_description,
        bank_name: formData.bank_name,
        bank_account_number: formData.bank_account_number,
        payment_reference: formData.payment_reference,
      };
      AMOUNT_FIELDS.forEach((field) => {
        payload[field] = toNumber(formData[field]).toFixed(2);
      });

      const res = initialData?.id
        ? await payrollService.updatePayroll(initialData.id, payload)
        : await payrollService.createPayroll(payload);

      notify.success(t('common.savedSuccessfully'));
      if (onSuccess) onSuccess(res.data);
    } catch (err) {
      console.error('Error saving payroll:', err);
      notify.error(err.response?.data?.detail || t('common.failedToSave'));
    } finally {
      setSaving(false);
    }
  };

  const amountField = (field, labelKey) => (
    <div className="form-group">
      <label className="form-label" htmlFor={`payroll-${field}`}>
        {t(labelKey)}
      </label>
      <input
        id={`payroll-${field}`}
        type="text"
        inputMode="decimal"
        value={formData[field]}
        onChange={(e) => handleAmountChange(field)(e.target.value)}
        disabled={locked}
        className="form-input"
      />
    </div>
  );

  const textField = (field, labelKey) => (
    <div className="form-group">
      <label className="form-label" htmlFor={`payroll-${field}`}>
        {t(labelKey)}
      </label>
      <input
        id={`payroll-${field}`}
        type="text"
        value={formData[field] || ''}
        onChange={(e) => handleChange(field)(e.target.value)}
        disabled={locked}
        className="form-input"
      />
    </div>
  );

  const dateField = (field, labelKey) => (
    <div className="form-group">
      <label className="form-label" htmlFor={`payroll-${field}`}>
        {t(labelKey)}
      </label>
      <input
        id={`payroll-${field}`}
        type="date"
        value={formData[field] || ''}
        onChange={(e) => handleChange(field)(e.target.value)}
        disabled={locked}
        className="form-input"
      />
      {errors[field] && <div className="form-error">{errors[field]}</div>}
    </div>
  );

  return (
    <form onSubmit={handleSubmit}>
      {locked && (
        <div className="alert alert-warning">{t('payroll.lockedNotice')}</div>
      )}

      <div className="grid-2">
        <div className="form-group">
          <label className="form-label" htmlFor="payroll-employee">
            {t('payroll.employee')}
          </label>
          <EmployeeAutocomplete
            employees={employees}
            selectedEmployee={selectedEmployee}
            onSelect={handleEmployeeSelect}
            disabled={locked}
          />
        </div>

        {initialData && (
          <div className="form-group" data-testid="payroll-summary">
            <h3>{initialData.employee_name}</h3>
            <p className="text-muted">{initialData.period_code}</p>
          </div>
        )}
      </div>

      <div className="grid-3">
        <div className="form-group">
          <label className="form-label" htmlFor="payroll-frequency">
            {t('payroll.frequency')}
          </label>
          <select
            id="payroll-frequency"
            value={formData.frequency}
            onChange={(e) => handleFrequencyChange(e.target.value)}
            disabled={locked}
            className="form-select"
          >
            <option value="">{t('filters.none')}</option>
            {FREQUENCIES.map((value) => (
              <option key={value} value={value}>
                {t(`payroll.frequencies.${value}`)}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="payroll-period_code">
            {t('payroll.period')}
          </label>
          <select
            id="payroll-period_code"
            value={formData.period_code}
            onChange={(e) => handleChange('period_code')(e.target.value)}
            disabled={locked}
            className="form-select"
          >
            <option value="">{t('filters.none')}</option>
            {periodOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>

        {dateField('payment_date', 'payroll.paymentDate')}
        {dateField('period_start', 'payroll.periodStart')}
        {dateField('period_end', 'payroll.periodEnd')}
        {textField('payment_reference', 'payroll.paymentReference')}
      </div>

      <div className="grid-3">
        {amountField('base_salary', 'payroll.baseSalary')}
        {amountField('extra_pay', 'payroll.extraPay')}
        {textField('bonus_description', 'payroll.bonusDescription')}
        {amountField('bonus', 'payroll.bonus')}
        {textField('bonus2_description', 'payroll.bonus2Description')}
        {amountField('bonus2', 'payroll.bonus2')}
        {textField('deduction_description', 'payroll.deductionDescription')}
        {amountField('deduction', 'payroll.deduction')}
        {amountField('social_security', 'payroll.socialSecurity')}
        {amountField('savings', 'payroll.savings')}
        {textField('bank_name', 'payroll.bankName')}
        {textField('bank_account_number', 'payroll.bankAccountNumber')}
      </div>

      <div className="totals-footer">
        <div className="totals-row">
          <span>{t('payroll.totalEarnings')}</span>
          <span data-testid="total-earnings">{totals.earnings.toFixed(2)}</span>
        </div>
        <div className="totals-row">
          <span>{t('payroll.totalDeductions')}</span>
          <span data-testid="total-deductions">{totals.deductions.toFixed(2)}</span>
        </div>
        <div className="totals-row grand">
          <span>{t('payroll.totalPayable')}</span>
          <span data-testid="total-payable">{totals.payable.toFixed(2)}</span>
        </div>
      </div>

      <div className="flex-center gap-15 mt-20">
        {!locked && (
          <button type="submit" disabled={saving} className="btn btn-primary">
            {saving ? t('common.saving') : t('common.save')}
          </button>
        )}
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          {t('payroll.backToList')}
        </button>
      </div>
    </form>
  );
};

export default PayrollForm;
